import {
  createCheckpoint,
  getCheckpoint,
  removeCheckpoint,
  saveCheckpoint
} from './checkpoint'
import { getMaxConcurrency, runUploadQueue } from './concurrency'
import { getCachedMultipartDigests } from './digest'
import { getMultipartPartPlan } from './rule'
import {
  createAbortError,
  isMultipartUploadAborted,
  throwIfAborted
} from './abort'

const UPLOAD_RETRY_COUNT = 3
const LIST_PARTS_PAGE_SIZE = 1000

function normalizeEtag (etag) {
  return String(etag || '').replace(/^"|"$/g, '').toUpperCase()
}

function isNoSuchUpload (error) {
  return Boolean(error && (
    error.code === 'NoSuchUpload' ||
    error.name === 'NoSuchUpload' ||
    error.reason === 'OSS_MULTIPART_UPLOAD_NOT_FOUND'
  ))
}

function getPartSize (fileSize, partSize, partNumber) {
  const start = (partNumber - 1) * partSize
  return Math.min(partSize, fileSize - start)
}

async function listAllParts (
  client,
  fileKey,
  uploadId,
  runOssOperation = operation => operation(client)
) {
  const parts = []
  let marker = 0
  let truncated = true

  while (truncated) {
    const result = await runOssOperation(currentClient => currentClient.listParts(fileKey, uploadId, {
      'max-parts': LIST_PARTS_PAGE_SIZE,
      'part-number-marker': marker
    }))
    const pageParts = (result.parts || []).map(part => ({
      number: Number(part.PartNumber === undefined ? part.number : part.PartNumber),
      etag: part.ETag === undefined ? part.etag : part.ETag,
      size: Number(part.Size === undefined ? part.size : part.Size)
    }))
    parts.push(...pageParts)
    truncated = result.isTruncated === true || result.isTruncated === 'true'
    if (truncated) {
      const nextMarker = Number(result.nextPartNumberMarker)
      if (!Number.isInteger(nextMarker) || nextMarker <= marker) {
        throw new Error('OSS 分片列表分页标记无效')
      }
      marker = nextMarker
    }
  }
  return parts
}

function reconcileCompletedParts ({ ossParts, fileSize, partSize, partCount, partMd5HexList }) {
  const completedParts = []
  ossParts.forEach(part => {
    if (
      Number.isInteger(part.number) &&
      part.number >= 1 &&
      part.number <= partCount &&
      part.size === getPartSize(fileSize, partSize, part.number) &&
      normalizeEtag(part.etag) === partMd5HexList[part.number - 1]
    ) {
      completedParts.push({
        number: part.number,
        etag: part.etag,
        size: part.size
      })
    }
  })
  return completedParts.sort((left, right) => left.number - right.number)
}

async function uploadPartWithRetry ({
  file,
  fileKey,
  uploadId,
  partNumber,
  partSize,
  contentMd5,
  signal,
  runOssOperation
}) {
  const start = (partNumber - 1) * partSize
  const end = Math.min(start + partSize, file.size)
  let lastError = null

  for (let attempt = 0; attempt < UPLOAD_RETRY_COUNT; attempt += 1) {
    throwIfAborted(signal)
    try {
      return await runOssOperation(currentClient => currentClient.uploadPart(fileKey, uploadId, partNumber, file, start, end, {
        headers: { 'Content-MD5': contentMd5 },
        mime: 'application/octet-stream'
      }))
    } catch (error) {
      if (isNoSuchUpload(error)) {
        throw error
      }
      lastError = error
    }
  }
  throw lastError
}

function reportProgress (onProgress, file, completedParts, partSize) {
  if (typeof onProgress !== 'function') {
    return
  }
  const uploadedBytes = completedParts.reduce((total, part) => (
    total + getPartSize(file.size, partSize, part.number)
  ), 0)
  onProgress({
    percent: Math.floor(uploadedBytes * 100 / file.size),
    file
  })
}

async function initiateCheckpoint ({
  file,
  fileKey,
  partSize,
  partCount,
  mimeType,
  checkpointScope,
  runOssOperation
}) {
  const result = await runOssOperation(currentClient => (
    currentClient.initMultipartUpload(fileKey, { mime: mimeType })
  ))
  const checkpoint = createCheckpoint({
    fileKey,
    file,
    uploadId: result.uploadId,
    partSize,
    partCount,
    scopeKey: checkpointScope
  })
  saveCheckpoint(checkpoint)
  return checkpoint
}

export async function uploadMultipartFile ({
  client,
  bucket,
  file,
  fileType,
  onProgress,
  checkpointScope,
  findExisting,
  getMetadata,
  completeUpload,
  signal,
  runOssOperation = operation => operation(client)
}) {
  throwIfAborted(signal)
  const { partSize, partCount } = getMultipartPartPlan(file.size)
  const digests = await getCachedMultipartDigests(file, partSize, partCount, signal)
  throwIfAborted(signal)
  const existingFileKey = typeof findExisting === 'function'
    ? await findExisting(digests.originSha1)
    : null
  if (existingFileKey) {
    removeCheckpoint(digests.fileKey, checkpointScope)
    if (typeof onProgress === 'function') {
      onProgress({ percent: 100, file })
    }
    return { name: existingFileKey }
  }
  const metadata = await getMetadata(digests.originSha1)
  throwIfAborted(signal)
  let checkpoint = getCheckpoint(
    digests.fileKey,
    file,
    partSize,
    partCount,
    checkpointScope
  )

  for (let rebuildAttempt = 0; rebuildAttempt <= 1; rebuildAttempt += 1) {
    try {
      if (!checkpoint) {
        checkpoint = await initiateCheckpoint({
          file,
          fileKey: digests.fileKey,
          partSize,
          partCount,
          mimeType: metadata.mime_type,
          checkpointScope,
          runOssOperation
        })
      }
      throwIfAborted(signal)
      const ossParts = await listAllParts(
        client,
        digests.fileKey,
        checkpoint.uploadId,
        runOssOperation
      )
      checkpoint.completedParts = reconcileCompletedParts({
        ossParts,
        fileSize: file.size,
        partSize,
        partCount,
        partMd5HexList: digests.partMd5HexList
      })
      checkpoint.status = checkpoint.completedParts.length === partCount
        ? 'completing'
        : 'uploading'
      saveCheckpoint(checkpoint)
      reportProgress(onProgress, file, checkpoint.completedParts, partSize)

      if (checkpoint.status === 'uploading') {
        const completedNumbers = new Set(checkpoint.completedParts.map(part => part.number))
        const pendingPartNumbers = Array.from(
          { length: partCount },
          (_, index) => index + 1
        ).filter(partNumber => !completedNumbers.has(partNumber))

        await runUploadQueue({
          partNumbers: pendingPartNumbers,
          concurrency: Math.min(2, getMaxConcurrency(partSize)),
          uploadPart: partNumber => uploadPartWithRetry({
            file,
            fileKey: digests.fileKey,
            uploadId: checkpoint.uploadId,
            partNumber,
            partSize,
            contentMd5: digests.partContentMd5List[partNumber - 1],
            signal,
            runOssOperation
          }),
          onPartComplete: (partNumber, result) => {
            checkpoint.completedParts.push({
              number: partNumber,
              etag: result.etag,
              size: getPartSize(file.size, partSize, partNumber)
            })
            checkpoint.completedParts.sort((left, right) => left.number - right.number)
            saveCheckpoint(checkpoint)
            reportProgress(onProgress, file, checkpoint.completedParts, partSize)
          },
          shouldStop: () => Boolean(signal && signal.aborted)
        })
        throwIfAborted(signal)
        checkpoint.status = 'completing'
        saveCheckpoint(checkpoint)
      }

      throwIfAborted(signal)
      const result = await completeUpload({
        bucket,
        file_key: digests.fileKey,
        upload_id: checkpoint.uploadId,
        size: file.size,
        file_type: fileType,
        user_type: 'manban_user',
        platform: 'pc',
        ...metadata
      }, signal)
      throwIfAborted(signal)
      removeCheckpoint(digests.fileKey, checkpointScope)
      if (typeof onProgress === 'function') {
        onProgress({ percent: 100, file })
      }
      return {
        ...result,
        name: result.key || digests.fileKey
      }
    } catch (error) {
      if (isMultipartUploadAborted(error) || (signal && signal.aborted)) {
        if (checkpoint) {
          try {
            await runOssOperation(currentClient => (
              currentClient.abortMultipartUpload(digests.fileKey, checkpoint.uploadId)
            ))
          } catch (abortError) {
            // 用户已明确取消，OSS 清理失败不应覆盖原始取消结果。
          }
        }
        removeCheckpoint(digests.fileKey, checkpointScope)
        throw createAbortError()
      }
      if (!isNoSuchUpload(error) || rebuildAttempt === 1) {
        throw error
      }
      removeCheckpoint(digests.fileKey, checkpointScope)
      checkpoint = null
    }
  }
}

export {
  isMultipartUploadAborted,
  isNoSuchUpload,
  listAllParts,
  normalizeEtag,
  reconcileCompletedParts
}
